import { DetectPlatformService } from '@alliance/shared/utils'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { PolymorpheusContent } from '@alliance/shared/polymorpheus'
import { asyncScheduler, BehaviorSubject, Subscription, timer } from 'rxjs'
import Siema from 'siema'
import { CarouselOptions } from '../models/options'
import { CarouselService, SiemaOnAppend, SiemaOnCurrentSlide, SiemaOnDestroy, SiemaOnGoTo, SiemaOnInsert, SiemaOnNext, SiemaOnPrepend, SiemaOnPrev, SiemaOnRemove } from '../services/carousel.service'

@Component({
  selector: 'alliance-carousel',
  styleUrls: ['./container.component.css'],
  templateUrl: './container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NgxSiemaContainerComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() public readonly next = new EventEmitter<{ currentSlide: number; selector?: string | HTMLElement }>()
  @Output() public readonly prev = new EventEmitter<{ currentSlide: number; selector?: string | HTMLElement }>()
  @Output() public readonly goTo = new EventEmitter<unknown>()
  @Output() public readonly remove = new EventEmitter<unknown>()
  @Output() public readonly insert = new EventEmitter<unknown>()
  @Output() public readonly prepend = new EventEmitter<unknown>()
  @Output() public readonly append = new EventEmitter<unknown>()
  @Output() public readonly destroy = new EventEmitter<unknown>()
  @Output() public readonly currentSlide = new EventEmitter<unknown>()
  @Input() public options: CarouselOptions = {}
  @Input() public customPrevIconContent: PolymorpheusContent
  @Input() public customNextIconContent: PolymorpheusContent
  public ngxClass!: string | HTMLElement
  public instance: (Siema & { innerElements?: HTMLElement[] }) | null = null
  public currentSlide$ = new BehaviorSubject<number>(0)
  public showPaginator$ = new BehaviorSubject<boolean>(false)
  private siemaSubscription = new Subscription()

  public constructor(private ngxSiemaService: CarouselService, private changeDetectorRef: ChangeDetectorRef, private platform: DetectPlatformService) {}

  public ngOnInit(): void {
    if (typeof this.options.selector === 'string') {
      this.ngxClass = this.options?.selector?.replace('.', '')
    }
    this.setNextSubscription()
    this.setPrevSubscription()
    this.setGoToSubscription()
    this.setRemoveSubscription()
    this.setInsertSubscription()
    this.setPrependSubscription()
    this.setAppendSubscription()
    this.setDestroySubscription()
    this.setCurrentSlideSubscription()
  }

  public ngAfterViewInit(): void {
    if (this.platform.isServer) {
      return
    }
    this.options.onChange = (): void => {
      this.changeDetectorRef.markForCheck()
      this.ngxSiemaService.currentSlide(this.instance?.currentSlide ?? 0, this.options.selector)
    }
    this.options.onInit = (): void => {
      this.changeDetectorRef.markForCheck()
    }
    this.instance = new Siema(this.options)
    this.showPaginator$.next(coerceBooleanProperty(this.options?.showPaginator && this.instance))
  }

  public ngOnDestroy(): void {
    if (this.instance) {
      timer(0, asyncScheduler).subscribe(() => {
        this.instance?.destroy()
        this.instance = null
      })
    }
    this.siemaSubscription.unsubscribe()
  }

  public trackByIndex(index: number): number {
    return index
  }

  public onGoTo(index: number): void {
    this.instance?.goTo(index, (): void =>
      this.goTo.emit({
        currentSlide: this.instance?.currentSlide || 0
      })
    )
  }

  public onRemove(index: number): void {
    this.instance?.remove(index, () =>
      this.remove.emit({
        currentSlide: this.instance?.currentSlide || 0
      })
    )
  }

  public prevSlide(): void {
    this.ngxSiemaService.prev(1, this.options.selector).subscribe()
  }

  public nextSlide(): void {
    this.ngxSiemaService.next(1, this.options.selector).subscribe()
  }

  private setCurrentSlideSubscription(): void {
    const subscription = this.ngxSiemaService.onCurrentSlide().subscribe(({ selector, listener }: SiemaOnCurrentSlide) => {
      if (!(this.compareSelectors(selector) && this.instance)) {
        return
      }
      this.currentSlide.emit({
        selector: this.options.selector,
        currentSlide: this.instance?.currentSlide || 0
      })
      this.currentSlide$.next(this.instance?.currentSlide || 0)
      listener.next({ listener, selector: this.options.selector, currentSlide: this.instance?.currentSlide || 0 })
    })
    this.siemaSubscription.add(subscription)
  }

  private setDestroySubscription(): void {
    const subscription = this.ngxSiemaService.onDestroy().subscribe(({ selector, listener, restoreMarkup }: SiemaOnDestroy) => {
      if (!(this.compareSelectors(selector) && this.instance)) {
        return
      }
      this.instance.destroy(restoreMarkup, (): void => {
        this.destroy.emit({
          selector: this.options.selector
        })
        listener.next({ selector: this.options.selector, listener })
      })
    })
    this.siemaSubscription.add(subscription)
  }

  private setAppendSubscription(): void {
    const subscription = this.ngxSiemaService.onAppend().subscribe(({ selector, item, listener }: SiemaOnAppend) => {
      if (!(this.compareSelectors(selector) && this.instance)) {
        return
      }
      this.instance.append(item, (): void => {
        this.append.emit({
          selector: this.options.selector,
          currentSlide: this.instance?.currentSlide || 0
        })
        listener.next({
          selector: this.options.selector,
          currentSlide: this.instance?.currentSlide || 0,
          listener,
          item
        })
      })
    })
    this.siemaSubscription.add(subscription)
  }

  private setPrependSubscription(): void {
    const subscription = this.ngxSiemaService.onPrepend().subscribe(({ item, listener, selector }: SiemaOnPrepend) => {
      if (!(this.compareSelectors(selector) && this.instance)) {
        return
      }
      this.instance.prepend(item, (): void => {
        this.prepend.emit({
          selector: this.options.selector,
          currentSlide: this.instance?.currentSlide || 0
        })
        listener.next({ selector: this.options.selector, currentSlide: this.instance?.currentSlide || 0, listener, item })
      })
    })
    this.siemaSubscription.add(subscription)
  }

  private setInsertSubscription(): void {
    const subscription = this.ngxSiemaService.onInsert().subscribe(({ item, listener, selector, index }: SiemaOnInsert) => {
      if (this.compareSelectors(selector) && this.instance) {
        this.instance.insert(item, index, (): void => {
          this.insert.emit({
            selector: this.options.selector,
            currentSlide: this.instance?.currentSlide || 0
          })
          listener.next({ selector: this.options.selector, currentSlide: this.instance?.currentSlide || 0, item, listener })
        })
      }
    })
    this.siemaSubscription.add(subscription)
  }

  private setRemoveSubscription(): void {
    const subscription = this.ngxSiemaService.onRemove().subscribe(({ selector, index, listener, item }: SiemaOnRemove) => {
      if (!(this.compareSelectors(selector) && this.instance)) {
        return
      }
      this.instance.remove(index, (): void => {
        this.remove.emit({
          selector: this.options.selector,
          currentSlide: this.instance?.currentSlide || 0
        })
        listener.next({ selector: this.options.selector, currentSlide: this.instance?.currentSlide || 0, listener, item })
      })
    })
    this.siemaSubscription.add(subscription)
  }

  private setGoToSubscription(): void {
    const subscription = this.ngxSiemaService.onGoTo().subscribe(({ selector, index, listener, item }: SiemaOnGoTo) => {
      if (!(this.compareSelectors(selector) && this.instance)) {
        return
      }
      this.instance.goTo(index, (): void => {
        this.goTo.emit({
          selector: this.options.selector,
          currentSlide: this.instance?.currentSlide || 0
        })
        listener.next({ selector: this.options.selector, currentSlide: this.instance?.currentSlide || 0, listener, item })
      })
    })
    this.siemaSubscription.add(subscription)
  }

  private setPrevSubscription(): void {
    const subscription = this.ngxSiemaService.onPrev().subscribe(({ selector, listener, numbers, item }: SiemaOnPrev) => {
      if (!(this.compareSelectors(selector) && this.instance)) {
        return
      }
      this.instance.prev(numbers, (): void => {
        this.prev.emit({
          selector: this.options.selector,
          currentSlide: this.instance?.currentSlide || 0
        })
        listener.next({
          selector: this.options.selector,
          currentSlide: this.instance?.currentSlide || 0,
          listener,
          item
        })
      })
    })
    this.siemaSubscription.add(subscription)
  }

  private setNextSubscription(): void {
    const subscription = this.ngxSiemaService.onNext().subscribe(({ selector, numbers, listener, item }: SiemaOnNext) => {
      if (!(this.compareSelectors(selector) && this.instance)) {
        return
      }
      this.instance.next(numbers, (): void => {
        this.next.emit({
          selector: this.options.selector,
          currentSlide: this.instance?.currentSlide || 0
        })
        listener.next({ selector: this.options.selector, currentSlide: this.instance?.currentSlide || 0, listener, item })
      })
    })
    this.siemaSubscription.add(subscription)
  }

  private compareSelectors(selector?: string | HTMLElement): boolean {
    return !selector || selector === this.options.selector
  }
}
